import { A } from '@ember/array';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import type AbilitiesService from 'ember-can/services/abilities';
import { not } from 'ember-truth-helpers';
import SecurityConfigureBlocklist from 'garaje/components/security/configure-blocklist';
import MessageBox from 'garaje/components/ui/classic/message-box';
import type BlacklistFilter from 'garaje/models/blacklist-filter';
import type Location from 'garaje/models/location';
import type User from 'garaje/models/user';
import type BlocklistFilterService from 'garaje/services/blocklist-filter';
import type { BlocklistFilterPage, BlocklistFilterPageRequest } from 'garaje/services/blocklist-filter';
import type CurrentLocationService from 'garaje/services/current-location';
import type StateService from 'garaje/services/state';
import { Permission } from 'garaje/utils/ui-permissions';
import { all } from 'rsvp';

interface SecurityComponentsConfigureBlocklistLocationSignature {
  Args: {
    /**
     * whether the location is connected to a multi-tenant property
     */
    isConnectedToProperty: boolean;
    /**
     * Route-seeded new filter for the "Add to blocklist" profile deep-link.
     * Forwarded to <SecurityConfigureBlocklist>, which opens the create modal
     * from it in its constructor.
     */
    initialCreateFilter?: BlacklistFilter | null;
    /**
     * Called when that deep-link create modal is dismissed, so the parent can
     * clear the seed + query param.
     */
    onCreateHandled?: () => void;
  };
}

export default class SecurityComponentsConfigureBlocklistLocation extends Component<SecurityComponentsConfigureBlocklistLocationSignature> {
  @service declare abilities: AbilitiesService;
  @service('blocklistFilter') declare blocklistFilterService: BlocklistFilterService;
  @service declare currentLocation: CurrentLocationService;
  @service declare state: StateService;

  determineAdminPermissionText =
    'Adding admins to the above alert list will give them permission to approve and deny visitors.';

  get canCreateBlocklist(): boolean {
    return this.abilities.can('create blocklist in security');
  }

  get canUpdateBlocklist(): boolean {
    return this.abilities.can('update blocklist in security');
  }

  get canUploadBlocklist(): boolean {
    return this.abilities.can('upload blocklist in security');
  }

  get blocklistFilters(): BlacklistFilter[] {
    const filters = this.blocklistFilterService.locationBlocklistFilters;
    return Array.isArray(filters) ? filters : filters.slice();
  }

  loadBlocklistContacts = async (): Promise<User[]> => {
    const contacts = await this.state.currentLocation.blocklistContacts;
    return contacts.slice();
  };

  loadBlocklistFilters = async (locationView?: 'this' | 'all'): Promise<BlacklistFilter[]> => {
    // 'this' scopes the table to filters applying at the current location (filter[applied-location]);
    // 'all' (or undefined, flag off) returns the whole company list (filter[company]).
    const appliedLocationId = locationView === 'this' ? this.state.currentLocation.id : undefined;
    const filters = await this.blocklistFilterService.queryByCompanyTask.perform(
      this.state.currentCompany.id,
      appliedLocationId,
    );
    return filters.slice();
  };

  // Location view applied server-side: narrowing the loaded pages instead would hide records on
  // pages not yet scrolled to.
  loadBlocklistFilterPage = async ({
    offset,
    limit,
    search,
    locationView,
  }: BlocklistFilterPageRequest): Promise<BlocklistFilterPage> => {
    const appliedLocationId = locationView === 'this' ? this.state.currentLocation.id : undefined;

    return this.blocklistFilterService.queryPage(
      { kind: 'company', companyId: this.state.currentCompany.id, appliedLocationId },
      { offset, limit, search },
    );
  };

  saveBlocklistContacts = async (blocklistContacts: User[]): Promise<Location> => {
    const contacts = await this.state.currentLocation.blocklistContacts;
    contacts.clear();
    contacts.pushObjects(A(blocklistContacts));

    return this.state.currentLocation.save();
  };

  searchUsers = async (term: string): Promise<User[]> => {
    const permissions = [
      Permission.VISITORS_ENTRY_BLOCKLIST_APPROVAL_REVIEW,
      Permission.VISITORS_INVITE_BLOCKLIST_APPROVAL_REVIEW,
    ];
    const confirmedUsers = true;
    const userRoles = await this.currentLocation.searchUsers.perform({ term, roles: [], permissions, confirmedUsers });
    return all(userRoles.map((role) => role.user));
  };

  <template>
    <SecurityConfigureBlocklist
      @blocklistFilters={{this.blocklistFilters}}
      @csvImportRoute="security.blocklist-import"
      @filterParentRelationshipName="location"
      @filterParent={{this.state.currentLocation}}
      @loadBlocklistContacts={{this.loadBlocklistContacts}}
      @loadBlocklistFilters={{this.loadBlocklistFilters}}
      @loadBlocklistFilterPage={{this.loadBlocklistFilterPage}}
      @saveBlocklistContacts={{this.saveBlocklistContacts}}
      @searchUsers={{this.searchUsers}}
      @readOnly={{not this.canUpdateBlocklist}}
      @hasCreatePermission={{this.canCreateBlocklist}}
      @hasUploadPermission={{this.canUploadBlocklist}}
      @initialCreateFilter={{@initialCreateFilter}}
      @onCreateHandled={{@onCreateHandled}}
    >
      <:admin-permission-text>{{this.determineAdminPermissionText}}</:admin-permission-text>

      <:message>
        {{#if @isConnectedToProperty}}
          <MessageBox
            @type="info"
            @header="Additional block list rules may apply."
            class="mt-8"
            data-test-property-blocklist-rules-may-apply-message
          >
            You are connected to a property, there may be additional block list rules applied.
          </MessageBox>
        {{/if}}
      </:message>
    </SecurityConfigureBlocklist>
  </template>
}
